import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  IconButton,
  styled,
  Avatar,
  Typography,
  Divider,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom"; // For navigation
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { getLocalStorageData } from "../../../shared/utils/localStorage";

// Styled components
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  cursor: "pointer", // Change cursor to pointer
  position: "relative",
  zIndex: "9",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "& .MuiTypography-h2": {
    color: "#035344",
    fontSize: ".775rem",
    marginLeft: "4px",
    fontWeight: "500",
    cursor: "pointer",
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  position: "absolute",
  zIndex: "9",
  top: "0",
  "& .MuiMenu-paper": {
    minWidth: "250px",
    borderRadius: "5px",
    backgroundColor: "#fff",
    boxShadow: "0px 0px 20px 0px rgba(0, 0, 0, 0.25)",
    maxHeight: "90vh", // Prevents the menu from exceeding viewport height
    overflowY: "auto", // Enables scrolling if content overflows
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  "&. MuiMenuItem-root": {
    fontSize: ".55rem", // Set the desired font size
  },
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
  "& .adminIcon": {
    cursor: "pointer",
  },
}));

const MenuHeader = styled("div")(({ theme }) => ({
  padding: theme.spacing(2, 2),
  color: theme.palette.primary.contrastText,
  borderBottom: "5px solid background: #7B7583;",
  display: "flex",
  alignItems: "center",
  gap: "18px",
  "& .MuiTypography-h3": {
    color: "#000",
    fontSize: ".875rem",
  },
  "& .MuiTypography-h4": {
    color: "#9C9C9C",
    fontSize: ".775rem",
    marginTop: "4px",
    fontWeight: "400",
  },
}));

const HoverDropdown: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate(); // For navigation

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  const userName = getLocalStorageData("user-name");
  const name = getLocalStorageData("name");
  const handleLogout = () => {
    // Clear localStorage and redirect to login page
    localStorage.clear(); // Clears all keys in localStorage
    // Alternatively, to remove specific keys:
    // localStorage.removeItem("authToken");

    navigate("/login"); // Redirect to login page
    setAnchorEl(null); // Close the menu
  };

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {/* Styled IconButton that triggers the dropdown */}
      <StyledIconButton
        aria-controls="hover-menu"
        aria-haspopup="true"
        sx={{ cursor: "pointer" }}
      >
        <Avatar sx={{ width: 32, height: 32, textTransform: "uppercase" }}>
        {name?.charAt(0)}
        </Avatar>
        <Typography variant="h2">{name}</Typography>
      </StyledIconButton>

      {/* Dropdown Menu */}
      <StyledMenu
        id="hover-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMouseLeave}
        MenuListProps={{
          onMouseEnter: () => setAnchorEl(anchorEl),
          onMouseLeave: handleMouseLeave,
        }}
      >
        <MenuHeader>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              fontSize: "1.85rem",
              textTransform: "uppercase",
            }}
          >
            {name?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="h3">{name}</Typography>
            <Typography variant="h4">{userName}</Typography>
          </Box>
        </MenuHeader>
        <Divider />
        <StyledMenuItem onClick={() => navigate("/settings/personal-details")}>
          {" "}
          <AccountCircleIcon />
          &nbsp; My Account
        </StyledMenuItem>
        <StyledMenuItem onClick={handleLogout}>
          {" "}
          <LogoutIcon />
          &nbsp; Logout
        </StyledMenuItem>
      </StyledMenu>
    </div>
  );
};

export default HoverDropdown;
